import { NgClass } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink, RouterLinkActive } from '@angular/router';

interface HeaderNavItem {
  label: string;
  type: 'link' | 'routerLink' | 'button';
  extraClass?: string;
}

export interface HeaderButtonNavItem extends HeaderNavItem {
  type: 'button';
  click: () => void;
}

export interface HeaderLinkNavItem extends HeaderNavItem {
  type: 'link';
  href?: string;
  click?: () => void;
}

export interface HeaderRouterLinkNavItem extends HeaderNavItem {
  type: 'routerLink';
  routerLink: string | string[];
  click?: () => void;
}

@Component({
  selector: 'app-menu',
  imports: [MatMenuModule, MatDividerModule, RouterLink, RouterLinkActive, NgClass],
  template: `
    <button
      class="wzb-btn-clean"
      [matMenuTriggerFor]="menu"
      (menuOpened)="menuOpened.emit()"
      (menuClosed)="menuClosed.emit()"
      type="button"
    >
      <ng-content />
    </button>

    <mat-menu class="mny-menu" #menu="matMenu">
      @for (group of items(); track $index) {
        @if ($index > 0) {
          <mat-divider />
        }

        @for (item of group; track $index) {
          @switch (item.type) {
            @case ('link') {
              <a
                [href]="item.href"
                [ngClass]="['menu__item--link', item.extraClass ?? '']"
                (click)="item.click && item.click()"
                target="_blank"
                mat-menu-item
                disableRipple
              >
                {{ item.label }}

                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11.9997 7.2135C11.8229 7.2135 11.6533 7.28373 11.5283 7.40876C11.4032 7.53378 11.333 7.70335 11.333 7.88016V12.6668C11.333 12.8436 11.2628 13.0132 11.1377 13.1382C11.0127 13.2633 10.8432 13.3335 10.6663 13.3335H3.33301C3.1562 13.3335 2.98663 13.2633 2.8616 13.1382C2.73658 13.0132 2.66634 12.8436 2.66634 12.6668V5.3335C2.66634 5.15669 2.73658 4.98712 2.8616 4.86209C2.98663 4.73707 3.1562 4.66683 3.33301 4.66683H8.11967C8.29649 4.66683 8.46605 4.59659 8.59108 4.47157C8.7161 4.34654 8.78634 4.17697 8.78634 4.00016C8.78634 3.82335 8.7161 3.65378 8.59108 3.52876C8.46605 3.40373 8.29649 3.3335 8.11967 3.3335H3.33301C2.80257 3.3335 2.29387 3.54421 1.91879 3.91928C1.54372 4.29436 1.33301 4.80306 1.33301 5.3335V12.6668C1.33301 13.1973 1.54372 13.706 1.91879 14.081C2.29387 14.4561 2.80257 14.6668 3.33301 14.6668H10.6663C11.1968 14.6668 11.7055 14.4561 12.0806 14.081C12.4556 13.706 12.6663 13.1973 12.6663 12.6668V7.88016C12.6663 7.70335 12.5961 7.53378 12.4711 7.40876C12.3461 7.28373 12.1765 7.2135 11.9997 7.2135ZM14.613 1.74683C14.5454 1.58393 14.4159 1.45448 14.253 1.38683C14.1729 1.35267 14.0868 1.33455 13.9997 1.3335H9.99967C9.82286 1.3335 9.65329 1.40373 9.52827 1.52876C9.40325 1.65378 9.33301 1.82335 9.33301 2.00016C9.33301 2.17697 9.40325 2.34654 9.52827 2.47157C9.65329 2.59659 9.82286 2.66683 9.99967 2.66683H12.393L5.52634 9.52683C5.46386 9.58881 5.41426 9.66254 5.38041 9.74378C5.34657 9.82502 5.32914 9.91216 5.32914 10.0002C5.32914 10.0882 5.34657 10.1753 5.38041 10.2565C5.41426 10.3378 5.46386 10.4115 5.52634 10.4735C5.58832 10.536 5.66205 10.5856 5.74329 10.6194C5.82453 10.6533 5.91167 10.6707 5.99967 10.6707C6.08768 10.6707 6.17482 10.6533 6.25606 10.6194C6.3373 10.5856 6.41103 10.536 6.47301 10.4735L13.333 3.60683V6.00016C13.333 6.17697 13.4032 6.34654 13.5283 6.47157C13.6533 6.59659 13.8229 6.66683 13.9997 6.66683C14.1765 6.66683 14.3461 6.59659 14.4711 6.47157C14.5961 6.34654 14.6663 6.17697 14.6663 6.00016V2.00016C14.6653 1.91304 14.6472 1.82698 14.613 1.74683Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
            }
            @case ('routerLink') {
              <a
                [routerLink]="item.routerLink"
                [class]="item.extraClass"
                (click)="item.click && item.click()"
                routerLinkActive="menu__item--active"
                mat-menu-item
                disableRipple
              >
                {{ item.label }}
              </a>
            }
            @case ('button') {
              <button [class]="item.extraClass" (click)="item.click()" type="button" mat-menu-item disableRipple>
                {{ item.label }}
              </button>
            }
          }
        }
      }
    </mat-menu>
  `,
})
export class MenuComponent {
  readonly menuOpened = output();
  readonly menuClosed = output();

  readonly items = input.required<(HeaderButtonNavItem | HeaderLinkNavItem | HeaderRouterLinkNavItem)[][]>();
}
